import React, { useState } from "react";
import { X, Mic, Volume2, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import client from "@/lib/api";
import { playTTS } from "@/lib/audio";
import { useRecorder } from "@/hooks/useRecorder";
import { Waveform } from "@/components/Waveform";
import { useLang } from "@/context/LangContext";

const wordColor = (s) => (s >= 80 ? "bg-emerald-100 text-emerald-700 border-emerald-300"
  : s >= 55 ? "bg-amber-100 text-amber-700 border-amber-300"
  : "bg-rose-100 text-rose-700 border-rose-300");

export default function PronounceModal({ phrase, translation = "", onClose }) {
  const rec = useRecorder();
  const { t } = useLang();
  const [state, setState] = useState("idle"); // idle | listening | scoring | result
  const [result, setResult] = useState(null);

  const toggle = async () => {
    if (state === "listening") {
      const res = await rec.stop();
      setState("scoring");
      if (!res || !res.blob) { setState("idle"); return; }
      try {
        const fd = new FormData();
        fd.append("file", res.blob, res.filename);
        fd.append("target", phrase);
        const { data } = await client.post("/pronounce", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setResult(data); setState("result");
      } catch { toast.error(t("couldnt_score")); setState("idle"); }
    } else if (state === "idle" || state === "result") {
      setResult(null);
      const ok = await rec.start();
      if (ok) setState("listening"); else toast.error(t("mic_permission"));
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-[430px] app-bg glass-strong rounded-t-3xl sm:rounded-3xl border border-white/10 p-6 animate-float-up" onClick={(e) => e.stopPropagation()} data-testid="pronounce-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg font-extrabold text-white">{t("pron_coach")}</h2>
          <button onClick={onClose} data-testid="pronounce-close" className="text-slate-400"><X size={22} /></button>
        </div>

        <div className="glass rounded-2xl p-4 text-center">
          <p className="font-heading text-2xl font-extrabold text-white leading-snug">{phrase}</p>
          {translation && <p className="text-slate-400 text-sm mt-1">{translation}</p>}
          <button onClick={() => playTTS(phrase)} data-testid="pronounce-listen" className="mt-3 inline-flex items-center gap-2 bg-white/5 text-cyan-300 font-semibold px-4 py-2 rounded-full press"><Volume2 size={16} /> {t("listen")}</button>
        </div>

        {state === "result" && result && (
          <div className="mt-5 animate-float-up" data-testid="pronounce-result">
            <div className="flex items-center gap-4">
              <div className="relative w-16 h-16 shrink-0">
                <svg viewBox="0 0 36 36" className="w-16 h-16 -rotate-90">
                  <path d="M18 2.5 a15.5 15.5 0 1 1 0 31 a15.5 15.5 0 1 1 0 -31" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="3.5" />
                  <path d="M18 2.5 a15.5 15.5 0 1 1 0 31 a15.5 15.5 0 1 1 0 -31" fill="none" stroke="url(#pc)" strokeWidth="3.5" strokeLinecap="round" strokeDasharray={`${result.overall}, 100`} />
                  <defs><linearGradient id="pc" x1="0" y1="0" x2="1" y2="1"><stop stopColor="#2563eb" /><stop offset="1" stopColor="#22aee8" /></linearGradient></defs>
                </svg>
                <span className="absolute inset-0 flex items-center justify-center font-heading font-extrabold text-white text-sm">{result.overall}%</span>
              </div>
              <p className="text-slate-300 text-sm flex-1">{result.feedback}</p>
            </div>

            <p className="text-xs font-semibold text-slate-400 mt-4 mb-2">{t("word_by_word")}</p>
            <div className="flex flex-wrap gap-2">
              {(result.words || []).map((w, i) => (
                <span key={i} className={`px-2.5 py-1 rounded-lg border text-sm font-medium ${wordColor(w.score)}`} data-testid={`pron-word-${i}`}>{w.word}</span>
              ))}
            </div>

            {(result.sounds || []).length > 0 && (
              <>
                <p className="text-xs font-semibold text-slate-400 mt-4 mb-2">{t("sounds_to_practice")}</p>
                <div className="space-y-2">
                  {result.sounds.map((s, i) => (
                    <div key={i} className="glass rounded-xl p-3 flex items-start gap-3" data-testid={`pron-sound-${i}`}>
                      <span className="mel-gradient text-white font-heading font-extrabold px-2.5 py-1 rounded-lg text-sm shrink-0">{s.sound}</span>
                      <span className="text-sm text-slate-300">{s.tip}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        <div className="mt-6 flex flex-col items-center gap-3">
          {(state === "listening") && <Waveform analyser={rec.analyser} active bars={28} tone="blue" className="w-full" />}
          {state === "scoring" && <p className="text-slate-400 flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> {t("scoring_pron")}</p>}
          <button onClick={toggle} disabled={state === "scoring"} data-testid="pronounce-record"
            className={`relative w-20 h-20 rounded-full flex items-center justify-center text-white press disabled:opacity-60 ${state === "listening" ? "bg-red-500" : "mel-gradient"} shadow-[0_12px_36px_-8px_rgba(37,99,235,0.65)]`}>
            {state === "listening" && <span className="absolute inset-0 rounded-full bg-red-400 mic-ring-out" />}
            {state === "result" ? <RotateCcw size={28} className="relative" /> : <Mic size={28} className="relative" />}
          </button>
          <p className="text-sm text-slate-400">{state === "listening" ? t("tap_to_stop") : state === "result" ? t("try_again_word") : t("tap_say_phrase")}</p>
        </div>
      </div>
    </div>
  );
}
